/* eslint-disable react/prop-types, camelcase, import/no-unresolved */
import { Box, Text } from "@ui-kit/components/index.es";
import I18n from "pages/_components/I18n";
import React from "react";
import NumberFormat from "react-number-format";
import { getAccountInfo } from "middleware/accounts";
import { getAccountDescription } from "util/accounts";
import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";
import { numberFormat } from "util/number";
import { truncateWithEllipsis } from "util/string";
import { isMvp5PendingTransactionsEnabled } from "util/transaction";
import getAccountChecksTransactionAdditionalLabel from "util/transactionAccountChecksAdditionalInfo";

const transfersRender = ({ clientUser }) => (
    <div>
        <I18n
            componentProps={{ className: "card__pending-transaction-data-creation" }}
            id="transaction.list.header.to"
        />
        <span>{clientUser?.firstName}</span>
    </div>
);

const transfersInternalRender = (_, __, environment) => (
    <div>
        <I18n
            componentProps={{ className: "card__pending-transaction-data-creation" }}
            id="transaction.list.header.to"
        />
        <span>{environment?.name}</span>
    </div>
);

const getFirstNotBlank = (...values) => values.find((value) => typeof value === "string" && value.trim().length > 0);

const pendingCardLabelText = (i18nKey) =>
    String(i18nUtils.get(i18nKey) || "")
        .replace(/:+\s*$/, "")
        .trim();

const formatDecimalWithSeparators = (num, thousandSeparator, decimalSeparator) => {
    const fixed = Number(num).toFixed(2);
    const parts = fixed.split(".");
    const intPart = parts[0] || "0";
    const decPart = parts[1] || "00";
    const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    return `${intWithSep}${decimalSeparator}${decPart}`;
};

const getEcomexBeneficiaryName = (data = {}) => {
    const transactionData = data?.transactionData || {};
    return getFirstNotBlank(
        transactionData?.beneficiaryName,
        transactionData?.nombreBeneficiario,
        data?.beneficiaryName,
        data?.nombreBeneficiario,
    );
};

const getExternalSendBeneficiaryName = (data = {}) => {
    const transactionData = data?.transactionData || {};
    return getFirstNotBlank(
        transactionData?.beneficiaryName,
        transactionData?.nombreBeneficiario,
        data?.beneficiaryName,
        data?.nombreBeneficiario,
    );
};

const getExternalSendActivityLabel = (idActivityOriginal) => {
    if (!idActivityOriginal || String(idActivityOriginal).trim() === "") {
        return "";
    }
    return i18nUtils.get(`activity.${idActivityOriginal}`, idActivityOriginal);
};

const formatSendExternalDebitFromApiAccount = (accountDebit) =>
    String(accountDebit.number).concat(
        " ",
        accountDebit.productType,
        " ",
        i18nUtils.get(`currency.label.${accountDebit.currency}`),
    );

const resolveSendExternalDebitRaw = (data = {}) => data.debitAccount ?? data.account;

const getDebitPayloadForAccountsInfo = (raw) => {
    if (raw == null || raw === "") {
        return null;
    }
    if (typeof raw === "string") {
        const t = raw.trim();
        return t.length > 0 ? t : null;
    }
    if (typeof raw === "object") {
        if (raw.idProduct != null && String(raw.idProduct).trim() !== "") {
            return raw.idProduct;
        }
        return raw;
    }
    return null;
};

const hasDebitReferenceForPendingSendExternal = (raw) => getDebitPayloadForAccountsInfo(raw) != null;

const syncDebitLabelFromLocalAccount = (raw) => {
    if (raw != null && typeof raw === "object" && raw.number !== undefined && raw.number !== null) {
        try {
            return getAccountDescription(raw);
        } catch (e) {
            return "";
        }
    }
    return "";
};

const SendExternalTransactionPendingBottom = ({ data = {}, isDesktop = true }) => {
    const debitRaw = resolveSendExternalDebitRaw(data);
    const hasDebitReference = hasDebitReferenceForPendingSendExternal(debitRaw);

    const debitFetchKey = React.useMemo(() => {
        if (debitRaw == null) {
            return "";
        }
        if (typeof debitRaw === "string") {
            return `s:${debitRaw.trim()}`;
        }
        if (typeof debitRaw === "object") {
            return `o:${debitRaw.idProduct ?? ""}|n:${debitRaw.number ?? ""}|cbu:${debitRaw.cbu ?? ""}`;
        }
        return "";
    }, [data.debitAccount, data.account]);

    const [debitLabel, setDebitLabel] = React.useState(() => syncDebitLabelFromLocalAccount(debitRaw));

    React.useEffect(() => {
        if (debitFetchKey === "") {
            setDebitLabel("");
            return undefined;
        }
        const raw = resolveSendExternalDebitRaw(data);
        const payload = getDebitPayloadForAccountsInfo(raw);
        if (!payload) {
            setDebitLabel("");
            return undefined;
        }
        setDebitLabel(syncDebitLabelFromLocalAccount(raw));
        let cancelled = false;
        getAccountInfo(payload)
            .then((response) => {
                const accountDebit = response?.data?.data?.account;
                if (!cancelled && accountDebit && accountDebit.number != null) {
                    setDebitLabel(formatSendExternalDebitFromApiAccount(accountDebit));
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [debitFetchKey]);

    const beneficiaryName = getExternalSendBeneficiaryName(data);
    const activityLabel = getExternalSendActivityLabel(data.idActivityOriginal);
    const showBeneficiary = Boolean(beneficiaryName && beneficiaryName.trim() !== "");
    const showActivity = activityLabel !== "";
    const showDebit = hasDebitReference;

    if (!showBeneficiary && !showActivity && !showDebit) {
        return null;
    }

    if (!isDesktop) {
        return (
            <div className="card__pending-transaction-data-bottom-send-external card__pending-transaction-data-bottom-send-external--stacked">
                {showBeneficiary && (
                    <div className="card__pending-transaction-send-external-line">
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.sendExternal.beneficiaryLabel"
                        />
                        <span>{beneficiaryName}</span>
                    </div>
                )}
                {showActivity && (
                    <div className="card__pending-transaction-send-external-line">
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.sendExternal.transactionTypeTagPrefix"
                        />
                        <span>{activityLabel}</span>
                    </div>
                )}
                {showDebit && (
                    <div className="card__pending-transaction-send-external-line">
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.sendExternal.debitAccountLabel"
                        />
                        <span>{debitLabel}</span>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="card__pending-transaction-data-bottom-send-external">
            {showBeneficiary && (
                <>
                    <I18n
                        componentProps={{
                            className: "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                        }}
                        id="multipleSignatures.sendExternal.beneficiaryLabel"
                    />
                    <span>{beneficiaryName}</span>
                </>
            )}
            {showActivity && (
                <>
                    {showBeneficiary && <span className="px-2">|</span>}
                    <I18n
                        componentProps={{
                            className: "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                        }}
                        id="multipleSignatures.sendExternal.transactionTypeTagPrefix"
                    />
                    <span>{activityLabel}</span>
                </>
            )}
            {showDebit && (
                <>
                    {(showBeneficiary || showActivity) && <span className="px-2">|</span>}
                    <I18n
                        componentProps={{
                            className: "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                        }}
                        id="multipleSignatures.sendExternal.debitAccountLabel"
                    />
                    <span>{debitLabel}</span>
                </>
            )}
        </div>
    );
};

const chargebackRecurrencyPendingBottom = (data = {}, isDesktop = true) => {
    const { idDebin } = data;
    const sellerCuit = data.sellerCuit || (data.sellerDetail && data.sellerDetail.documentNumber) || "";
    const idStr = idDebin != null && String(idDebin).trim() !== "" ? String(idDebin).trim() : "";
    const cuitStr = sellerCuit != null && String(sellerCuit).trim() !== "" ? String(sellerCuit).trim() : "";
    if (!idStr && !cuitStr) {
        return null;
    }
    if (!isDesktop) {
        return (
            <div className="d-flex flex-column align-items-stretch w-100 card__pending-transaction-ecomex-mobile-stack">
                {idStr !== "" && (
                    <div className="card__pending-transaction-send-external-line">
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.chargebackRecurrency.idRecurrenceLabel"
                        />
                        <span>{` ${idStr}`}</span>
                    </div>
                )}
                {cuitStr !== "" && (
                    <div className="card__pending-transaction-send-external-line">
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.chargebackRecurrency.cuitVendorLabel"
                        />
                        <span>{` ${cuitStr}`}</span>
                    </div>
                )}
            </div>
        );
    }
    return (
        <div>
            {idStr !== "" && (
                <>
                    <I18n
                        componentProps={{
                            className: "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                        }}
                        id="multipleSignatures.chargebackRecurrency.idRecurrenceLabel"
                    />
                    <span>{` ${idStr}`}</span>
                </>
            )}
            {idStr !== "" && cuitStr !== "" && <span className="px-2">|</span>}
            {cuitStr !== "" && (
                <>
                    <I18n
                        componentProps={{
                            className: "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                        }}
                        id="multipleSignatures.chargebackRecurrency.cuitVendorLabel"
                    />
                    <span>{` ${cuitStr}`}</span>
                </>
            )}
        </div>
    );
};

const TransactionCustomInfo = {
    "negotiate.echeq.activity": ({ echeqList }) => (
        <div>
            <I18n componentProps={{ className: "card__pending-transaction-data-creation" }} id="echeq.received.from" />
            <span>{echeqList?.map((echeq) => echeq.from?.emisorRazonSocial)}</span>
            {echeqList && (
                <>
                    <span className="px-2">|</span>
                    <I18n
                        componentProps={{ className: "card__pending-transaction-data-creation" }}
                        id="echeqs.cheque_numero"
                    />
                    {echeqList.map(({ cheque_numero }, index) => (
                        <>
                            {index !== 0 && <span className="px-2">|</span>}
                            <span> {cheque_numero} </span>
                        </>
                    ))}
                </>
            )}
        </div>
    ),
    "endorse.echeq.activity": ({ echeqList, beneficiaryName }) =>
        echeqList?.map((echeq) => (
            <div>
                <I18n
                    componentProps={{ className: "card__pending-transaction-data-creation" }}
                    id="echeq.received.from"
                />
                <span>{echeq.from?.emisorRazonSocial}</span>
                <span className="px-2">|</span>
                <I18n
                    componentProps={{ className: "card__pending-transaction-data-creation" }}
                    id="transaction.list.header.to"
                />
                <span>{beneficiaryName}</span>
                {echeqList && (
                    <>
                        <span className="px-2">|</span>
                        <I18n
                            componentProps={{ className: "card__pending-transaction-data-creation" }}
                            id="echeqs.cheque_numero"
                        />
                        {echeqList.map(({ cheque_numero }, index) => (
                            <>
                                {index !== 0 && <span className="px-2">|</span>}
                                <span> {cheque_numero} </span>
                            </>
                        ))}
                    </>
                )}
            </div>
        )),
    "emit.echeq.activity": ({ beneficiaryName, echeqNumeration }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.list.header.to"
            />
            <span>{beneficiaryName}</span>
            {echeqNumeration && (
                <>
                    <span className="px-2">|</span>
                    <I18n
                        componentProps={{ className: "card__pending-transaction-data-creation" }}
                        id="echeqs.cheque_numero"
                    />
                    {JSON.parse(echeqNumeration).map(({ cheque_numero }, index) => (
                        <>
                            {index !== 0 && <span className="px-2">|</span>}
                            <span> {cheque_numero} </span>
                        </>
                    ))}
                </>
            )}
        </div>
    ),
    "external.emit.echeq.activity": ({ beneficiaryName, echeqNumeration }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.list.header.to"
            />
            <span>{beneficiaryName}</span>
            {echeqNumeration && (
                <>
                    <span className="px-2">|</span>
                    <I18n
                        componentProps={{ className: "card__pending-transaction-data-creation" }}
                        id="echeqs.cheque_numero"
                    />
                </>
            )}
        </div>
    ),
    "external.emit.massive.echeq.activity": ({ batchId, reference, echeqsQuantity }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="echeq.massive.emit.summary.idBatch.label"
            />
            <span>{batchId}</span>
            {echeqsQuantity != null && (
                <>
                    <span className="px-2">|</span>
                    <I18n
                        componentProps={{ className: "card__pending-transaction-data-creation" }}
                        id="echeq.massive.emit.summary.quantity.label"
                    />
                    <span>{echeqsQuantity}</span>
                </>
            )}
            {reference && (
                <>
                    <span className="px-2">|</span>
                    <I18n
                        componentProps={{ className: "card__pending-transaction-data-creation" }}
                        id="echeq.massive.emit.summary.batchReference.label"
                    />
                    <span>{reference}</span>
                </>
            )}
        </div>
    ),
    "cesion.echeq.activity": ({ echeqList, beneficiaryName }) =>
        echeqList?.map((echeq) => (
            <div>
                <I18n
                    componentProps={{ className: "card__pending-transaction-data-creation" }}
                    id="echeq.received.from"
                />
                <span>{echeq.from?.emisorRazonSocial}</span>
                <span className="px-2">|</span>
                <I18n
                    componentProps={{ className: "card__pending-transaction-data-creation" }}
                    id="transaction.list.header.to"
                />
                <span>{beneficiaryName}</span>
                {echeqList && (
                    <>
                        <span className="px-2">|</span>
                        <I18n
                            componentProps={{ className: "card__pending-transaction-data-creation" }}
                            id="echeqs.cheque_numero"
                        />
                        {echeqList.map(({ cheque_numero }, index) => (
                            <>
                                {index !== 0 && <span className="px-2">|</span>}
                                <span> {cheque_numero} </span>
                            </>
                        ))}
                    </>
                )}
            </div>
        )),
    "accept.return.echeqs": ({ solicitantName, echeqList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="table.acceptReturn.solicitante"
            />
            <span>{solicitantName}</span>
            {echeqList && (
                <>
                    <span className="px-2">|</span>
                    <I18n
                        componentProps={{ className: "card__pending-transaction-data-creation" }}
                        id="echeqs.cheque_numero"
                    />
                    {echeqList.map(({ cheque_numero }, index) => (
                        <>
                            {index !== 0 && <span className="px-2">|</span>}
                            <span> {cheque_numero} </span>
                        </>
                    ))}
                </>
            )}
        </div>
    ),
    "accounts.opening": ({ currency, accountType }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.list.header.aditionalInfo"
            />
            <span>{`${i18nUtils.get(`accounts.new.account.accountType.${accountType}`)} ${i18nUtils.get(
                `accounts.new.account.currency.${currency}`,
            )}`}</span>
        </div>
    ),
    "account.checks": (data) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.list.header.aditionalInfo"
            />
            <span>{getAccountChecksTransactionAdditionalLabel(data)}</span>
        </div>
    ),
    "scheduled.transactions.cancel": ({ clientUser }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.list.header.to"
            />
            <span>{clientUser?.firstName}</span>
        </div>
    ),
    "delete.paymentAFIP": ({ taxpayerCUIT }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="pay.paymentAFIP.taxpayerCUIT"
            />
            <span>{taxpayerCUIT}</span>
        </div>
    ),
    "payments.makePayment": ({ summaryInfo }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="services.newPayment.entities.label"
            />
            <span>{summaryInfo?.selectedEntity?.description}</span>
        </div>
    ),
    "pay.paymentAFIP": ({ paymentAFIP }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="pay.paymentAFIP.taxpayerCUIT"
            />
            <span>{paymentAFIP?.contributorsCUIT}</span>
        </div>
    ),
    "pay.multiline.send": ({ typePayment }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="payments.payThirdParties.newPayment.type.label"
            />
            <span>
                {typePayment === "PH"
                    ? i18nUtils.get("payments.payThirdParties.newPayment.salaryPayments.label")
                    : i18nUtils.get("payments.payThirdParties.newPayment.supplierPayments.label")}
            </span>
        </div>
    ),
    "change.status.debin.recurrency": ({ sellerName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="table.acceptReturn.solicitante"
            />
            <span>{sellerName}</span>
        </div>
    ),
    "load.debin.recurrency": ({ sellerName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="table.acceptReturn.solicitante"
            />
            <span>{sellerName}</span>
        </div>
    ),
    "credin.sentRequest.send": ({ cbuList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="requestCredin.summary.owner.label"
            />
            {Array.isArray(cbuList)
                ? cbuList?.map(({ headline }, index) => (
                      <>
                          {index !== 0 ? <span className="px-2">|</span> : ""}
                          <span> {headline} </span>
                      </>
                  ))
                : undefined}
        </div>
    ),
    "credinCVU.sentRequest.send": ({ cbuList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="requestCredin.summary.owner.label"
            />
            {Array.isArray(cbuList)
                ? cbuList?.map(({ headline }, index) => (
                      <>
                          {index !== 0 ? <span className="px-2">|</span> : ""}
                          <span> {headline} </span>
                      </>
                  ))
                : undefined}
        </div>
    ),
    "debin.incoming.accept.send": ({ sellerDetail }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="table.acceptReturn.solicitante"
            />
            <span>{sellerDetail?.titular}</span>
        </div>
    ),
    "debinCVU.incoming.accept.send": ({ sellerDetail }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="table.acceptReturn.solicitante"
            />
            <span>{sellerDetail?.titular}</span>
        </div>
    ),
    "administration.signatures.delete.send": ({ signatureLevelsCounts }, administrationScheme) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="administration.signatures.list.item.schema.label"
            />
            <span>
                {administrationScheme === "medium"
                    ? `${i18nUtils.get("administration.signatures.create.medium.confirm.signersCount")} ` +
                      `${signatureLevelsCounts.A}`
                    : Object.keys(signatureLevelsCounts).reduce(
                          (result, key) => result + key.toString().repeat(signatureLevelsCounts[key]),
                          "",
                      )}
            </span>
        </div>
    ),
    "administration.signatures.modify.send": ({ signatureLevelsCounts }, administrationScheme) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="administration.signatures.list.item.schema.label"
            />
            <span>
                {administrationScheme === "medium"
                    ? `${i18nUtils.get("administration.signatures.create.medium.confirm.signersCount")} ` +
                      `${signatureLevelsCounts.A}`
                    : Object.keys(signatureLevelsCounts).reduce(
                          (result, key) => result + key.toString().repeat(signatureLevelsCounts[key]),
                          "",
                      )}
            </span>
        </div>
    ),
    "administration.signatures.create.send": ({ signatureLevelsCounts }, administrationScheme) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="administration.signatures.list.item.schema.label"
            />
            <span>
                {administrationScheme === "medium"
                    ? `${i18nUtils.get("administration.signatures.create.medium.confirm.signersCount")} ` +
                      `${signatureLevelsCounts.A}`
                    : Object.keys(signatureLevelsCounts).reduce(
                          (result, key) => result + key.toString().repeat(signatureLevelsCounts[key]),
                          "",
                      )}
            </span>
        </div>
    ),
    "administration.restrictions.user.delete.send": ({ userFullName }) => (
        <div>
            <span className="card__pending-transaction-data-creation">{`${i18nUtils.get(
                "transaction.ticket.user",
            )}/${i18nUtils.get("transaction.ticket.group")}`}</span>
            <span>{userFullName}</span>
        </div>
    ),
    "administration.restrictions.manage.send": ({ userName }) => (
        <div>
            <span className="card__pending-transaction-data-creation">{`${i18nUtils.get(
                "transaction.ticket.user",
            )}/${i18nUtils.get("transaction.ticket.group")}`}</span>
            <span>{userName || `${i18nUtils.get("administration.restrictions.restrictions.environment")}`}</span>
        </div>
    ),
    "administration.simple.modify.permissions.send": ({ userFullName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            <span>{userFullName}</span>
        </div>
    ),
    "administration.medium.modify.permissions.send": ({ userFullName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            <span>{userFullName}</span>
        </div>
    ),
    "administration.medium.modify.signature.send": ({ userFullName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            <span>{userFullName}</span>
        </div>
    ),
    "administration.medium.modify.channels.send": ({ enabledChannels }) => (
        <div>
            <I18n componentProps={{ className: "card__pending-transaction-data-creation" }} id="tableHeader.channel" />
            {Array.isArray(enabledChannels)
                ? enabledChannels
                      .filter((channel) => channel !== "all")
                      .map((channel, index) => (
                          <>
                              {index !== 0 ? <span className="px-2">|</span> : ""}
                              <I18n id={`channels.${channel}`} component="span" />
                          </>
                      ))
                : ""}
        </div>
    ),
    "administration.users.invite.send": ({ firstName, lastName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            <span>{`${firstName} ${lastName}`}</span>
        </div>
    ),
    "administration.users.update": ({ userFullName }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            <span>{userFullName}</span>
        </div>
    ),
    "administration.users.blockunblock.send": ({ userNameList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            {Array.isArray(userNameList)
                ? userNameList.map((userName, index) => (
                      <>
                          {index !== 0 ? <span className="px-2">|</span> : ""}
                          <span> {userName} </span>
                      </>
                  ))
                : ""}
        </div>
    ),
    "administration.users.delete.send": ({ userNameList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.user"
            />
            {Array.isArray(userNameList)
                ? userNameList.map((userName, index) => (
                      <>
                          {index !== 0 ? <span className="px-2">|</span> : ""}
                          <span> {userName} </span>
                      </>
                  ))
                : ""}
        </div>
    ),
    "administration.groups.delete.send": ({ groupNameList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.group"
            />
            {Array.isArray(groupNameList)
                ? groupNameList.map((groupName, index) => (
                      <>
                          {index !== 0 ? <span className="px-2">|</span> : ""}
                          <span> {groupName} </span>
                      </>
                  ))
                : ""}
        </div>
    ),
    "administration.groups.blockunblock.send": ({ groupNameList }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.group"
            />
            {Array.isArray(groupNameList)
                ? groupNameList.map((groupName, index) => (
                      <>
                          {index !== 0 ? <span className="px-2">|</span> : ""}
                          <span> {groupName} </span>
                      </>
                  ))
                : ""}
        </div>
    ),
    "administration.advanced.group.modify.send": ({ name }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.group"
            />
            <span>{name}</span>
        </div>
    ),
    "administration.groups.modify.send": ({ name }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.group"
            />
            <span>{name}</span>
        </div>
    ),
    "administration.advanced.group.create.send": ({ name }) => (
        <div>
            <I18n
                componentProps={{ className: "card__pending-transaction-data-creation" }}
                id="transaction.ticket.group"
            />
            <span>{name}</span>
        </div>
    ),
    "ecomex.send.accept.transaction": (data = {}, _, __, isDesktop = true) => {
        const transactionData = data?.transactionData || {};
        const showDivisaAmount = configUtils.getBoolean("frotend.show.divisa.amount.Ecomex", false);
        const showBeneficiary = configUtils.getBoolean("frontend.show.beneficiary.ecomex", false);
        const beneficiaryMaxLength = configUtils.getInteger("frontend.show.beneficiary.ecomex.maxLength", 100);
        const beneficiaryName = getEcomexBeneficiaryName(data);
        const beneficiaryNameToShow = truncateWithEllipsis(beneficiaryName, beneficiaryMaxLength);
        const reqCur = transactionData?.requestCurrency;
        const reqAmt = transactionData?.requestAmount;
        const hasCurrency = showDivisaAmount && reqCur != null && String(reqCur).trim() !== "";
        const amountParsed =
            typeof reqAmt === "number" && Number.isFinite(reqAmt)
                ? reqAmt
                : parseFloat(
                      String(reqAmt ?? "")
                          .trim()
                          .replace(/\s/g, "")
                          .replace(",", "."),
                      10,
                  );
        const hasAmount = showDivisaAmount && Number.isFinite(amountParsed);
        const { decimalSeparator, thousandSeparator } = numberFormat(i18nUtils.getLang());
        const showBeneficiaryBlock = showBeneficiary && beneficiaryNameToShow;

        if (!showDivisaAmount && !showBeneficiaryBlock) {
            return null;
        }

        if (!showDivisaAmount && showBeneficiaryBlock) {
            const beneficiaryLabel = pendingCardLabelText("ecomex.transaction.card.beneficiaryName");
            const beneficiaryOnly = (
                <Box flex gap={1} align="center" flexWrap="wrap">
                    <Text
                        variant="captionBold"
                        className="card__pending-transaction-data-creation"
                        css={{ fontSize: "0.75rem", margin: 0 }}>
                        {beneficiaryLabel}
                    </Text>
                    <Text variant="captionBold" css={{ fontSize: "0.75rem", margin: 0 }}>
                        {beneficiaryNameToShow}
                    </Text>
                </Box>
            );
            const beneficiaryOnlyMobile = (
                <Text
                    variant="captionBold"
                    className="card__pending-transaction-ecomex-combined-kv"
                    css={{ fontSize: "0.75rem", margin: 0 }}>
                    {`${beneficiaryLabel}: ${beneficiaryNameToShow}`}
                </Text>
            );
            if (!isDesktop) {
                return (
                    <div className="d-flex flex-column card__pending-transaction-ecomex-mobile-stack">
                        {beneficiaryOnlyMobile}
                    </div>
                );
            }
            return beneficiaryOnly;
        }

        const currencyLabel = pendingCardLabelText("ecomex.transaction.data.requestCurrency");
        const amountLabel = pendingCardLabelText("ecomex.transaction.data.requestAmount");
        const beneficiaryLabelFull = pendingCardLabelText("ecomex.transaction.card.beneficiaryName");

        const currencyRowMobile = hasCurrency ? (
            <Text
                variant="captionBold"
                className="card__pending-transaction-ecomex-combined-kv"
                css={{ fontSize: "0.75rem", margin: 0 }}>
                {`${currencyLabel}: ${String(reqCur).trim()}`}
            </Text>
        ) : null;

        const amountRowMobile = hasAmount ? (
            <NumberFormat
                value={amountParsed}
                displayType="text"
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                decimalScale={2}
                fixedDecimalScale
                renderText={(formattedValue) => (
                    <Text
                        variant="captionBold"
                        className="card__pending-transaction-ecomex-combined-kv"
                        css={{ fontSize: "0.75rem", margin: 0 }}>
                        {`${amountLabel}: ${formattedValue}`}
                    </Text>
                )}
            />
        ) : null;

        const beneficiaryRowMobile = showBeneficiaryBlock ? (
            <Text
                variant="captionBold"
                className="card__pending-transaction-ecomex-combined-kv"
                css={{ fontSize: "0.75rem", margin: 0 }}>
                {`${beneficiaryLabelFull}: ${beneficiaryNameToShow}`}
            </Text>
        ) : null;

        if (!isDesktop) {
            return (
                <div className="d-flex flex-column align-items-stretch w-100 card__pending-transaction-ecomex-mobile-stack">
                    {currencyRowMobile}
                    {amountRowMobile}
                    {beneficiaryRowMobile}
                </div>
            );
        }

        const ecomexDesktopParts = [];
        if (hasCurrency) {
            ecomexDesktopParts.push(`${currencyLabel}: ${String(reqCur).trim()}`);
        }
        if (hasAmount) {
            ecomexDesktopParts.push(
                `${amountLabel}: ${formatDecimalWithSeparators(amountParsed, thousandSeparator, decimalSeparator)}`,
            );
        }
        if (showBeneficiaryBlock) {
            ecomexDesktopParts.push(`${beneficiaryLabelFull}: ${beneficiaryNameToShow}`);
        }
        const ecomexBottomLine = (
            <div className="card__pending-transaction-ecomex-desktop-flow">
                <Text variant="captionBold" css={{ fontSize: "0.75rem", margin: 0, lineHeight: 1.35 }}>
                    {ecomexDesktopParts.join("\u00A0|\u00A0")}
                </Text>
            </div>
        );

        return ecomexBottomLine;
    },
    "transfers.internal.send": transfersInternalRender,
    "transfers.internal.tad.send": transfersInternalRender,
    "transfers.thirdParties.send": transfersRender,
    "transfersCVU.thirdParties.send": transfersRender,
    "transfers.thirdParties.tad.send": transfersRender,
    "transfers.toContact.send": transfersRender,
    "transfersCVU.toContact.send": transfersRender,
    "transfers.toContact.tad.send": transfersRender,
    "send.external.transaction": (data, _, __, isDesktop) => (
        <SendExternalTransactionPendingBottom data={data} isDesktop={isDesktop} />
    ),
    "echeq.request-certificate.action": (data = {}, _, __, isDesktop = true) => {
        const fromList = data.echeqList?.[0];
        const raw = fromList ? { ...data, ...fromList } : data;
        const chequeNumero =
            raw.cheque_numero === undefined || raw.cheque_numero === null ? "" : String(raw.cheque_numero);
        const echeqId = raw.cod_visualizacion || raw.cheque_id || "";
        if (!chequeNumero.trim() && !String(echeqId).trim()) {
            return null;
        }
        if (!isDesktop) {
            return (
                <div className="d-flex flex-column align-items-stretch w-100 card__pending-transaction-ecomex-mobile-stack">
                    {chequeNumero !== "" && (
                        <div className="card__pending-transaction-send-external-line">
                            <I18n
                                componentProps={{
                                    className:
                                        "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                                }}
                                id="multipleSignatures.echeqRequestCertificate.chequeNumberLabel"
                            />
                            <span>{` ${chequeNumero}`}</span>
                        </div>
                    )}
                    {echeqId !== "" && (
                        <div className="card__pending-transaction-send-external-line">
                            <I18n
                                componentProps={{
                                    className:
                                        "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                                }}
                                id="multipleSignatures.echeqRequestCertificate.echeqIdTagPrefix"
                            />
                            <span>{` ${echeqId}`}</span>
                        </div>
                    )}
                </div>
            );
        }
        return (
            <div>
                {chequeNumero !== "" && (
                    <>
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.echeqRequestCertificate.chequeNumberLabel"
                        />
                        <span>{` ${chequeNumero}`}</span>
                    </>
                )}
                {chequeNumero !== "" && echeqId !== "" && <span className="px-2">|</span>}
                {echeqId !== "" && (
                    <>
                        <I18n
                            componentProps={{
                                className:
                                    "card__pending-transaction-data-creation card__pending-transaction-data-plain",
                            }}
                            id="multipleSignatures.echeqRequestCertificate.echeqIdTagPrefix"
                        />
                        <span>{` ${echeqId}`}</span>
                    </>
                )}
            </div>
        );
    },
    "chargeback.recurrency": (data, _, __, isDesktop) => chargebackRecurrencyPendingBottom(data, isDesktop),
    "temporary.limit.increase.request": (data = {}, _, __, isDesktop = true) => {
        if (!isMvp5PendingTransactionsEnabled()) {
            return "";
        }
        const { reason, duration, currency, limit, temporaryLimit } = data;
        const limitRaw =
            temporaryLimit !== undefined && temporaryLimit !== null && temporaryLimit !== "" ? temporaryLimit : limit;
        const reasonLabel = reason ? i18nUtils.get(`caps.reason.${reason}`, reason) : "";
        const limitQty = (() => {
            if (limitRaw === undefined || limitRaw === null || limitRaw === "") {
                return 0;
            }
            const n = Number(limitRaw);
            return Number.isFinite(n) ? n : 0;
        })();
        const currencyCode =
            currency !== undefined && currency !== null && String(currency).trim() !== "" ? String(currency) : "0";
        const durationHours = duration != null && String(duration).trim() !== "" ? String(duration) : "";
        const { decimalSeparator, thousandSeparator } = numberFormat(i18nUtils.getLang());
        const currencyLabel = i18nUtils.get(`currency.label.${currencyCode}`);
        const limitFormatted = (
            <NumberFormat
                value={limitQty}
                displayType="text"
                thousandSeparator={thousandSeparator}
                decimalSeparator={decimalSeparator}
                decimalScale={2}
                fixedDecimalScale
                renderText={(formattedValue) => <span>{` ${currencyLabel} ${formattedValue}`}</span>}
            />
        );
        if (!isDesktop) {
            return (
                <div className="card__pending-transaction-mvp5-limit-mobile-stack">
                    <div className="card__pending-transaction-mvp5-limit-mobile-row">
                        <I18n
                            componentProps={{ className: "card__pending-transaction-mvp5-inline-label" }}
                            id="transactions.pending.card.temporaryLimitIncrease.motivo"
                        />
                        <span>{reasonLabel ? ` ${reasonLabel}` : ""}</span>
                    </div>
                    <div className="card__pending-transaction-mvp5-limit-mobile-row">
                        <I18n
                            componentProps={{ className: "card__pending-transaction-mvp5-inline-label" }}
                            id="transactions.pending.card.temporaryLimitIncrease.temporaryLimit"
                        />
                        {limitFormatted}
                    </div>
                    <div className="card__pending-transaction-mvp5-limit-mobile-row">
                        <I18n
                            componentProps={{ className: "card__pending-transaction-mvp5-inline-label" }}
                            id="transactions.pending.card.temporaryLimitIncrease.vigencia"
                        />
                        <span>{durationHours ? ` ${durationHours}h` : ""}</span>
                    </div>
                </div>
            );
        }
        return (
            <div>
                <I18n
                    componentProps={{ className: "card__pending-transaction-mvp5-inline-label" }}
                    id="transactions.pending.card.temporaryLimitIncrease.motivo"
                />
                <span>{reasonLabel ? ` ${reasonLabel}` : ""}</span>
                <span className="px-2">|</span>
                <I18n
                    componentProps={{ className: "card__pending-transaction-mvp5-inline-label" }}
                    id="transactions.pending.card.temporaryLimitIncrease.temporaryLimit"
                />
                {limitFormatted}
                <span className="px-2">|</span>
                <I18n
                    componentProps={{ className: "card__pending-transaction-mvp5-inline-label" }}
                    id="transactions.pending.card.temporaryLimitIncrease.vigencia"
                />
                <span>{durationHours ? ` ${durationHours}h` : ""}</span>
            </div>
        );
    },
};

export default TransactionCustomInfo;
